import React from "react";
import { useNavigate } from "react-router-dom";
import CurrentSession from "data/userSession";
import useCustomSnackbar from "hooks/customSnackbar";
import { Button } from "@mui/material";
import AnalyticsService from "data/firestore/analytics";
import remoteConfigService from "data/firestore/remoteConfig";
import Logger from "utils/logger";

const Gatekeep = ({ children, featureName = null }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useCustomSnackbar();

  const handleClick = (event) => {
    const user = CurrentSession.user;

    // Check if gatekeepers are globally disabled via Remote Config
    if (!remoteConfigService.shouldEnforceGatekeepers) {
      Logger.debug(
        `Gatekeep: Gatekeepers disabled globally for feature: ${
          featureName || "unknown"
        }`
      );
      return true;
    }

    // If user is a definitive member, always allow access
    if (user && user.supporter) {
      return true;
    }

    // If user is not signed in, allow the original click handler to run
    // so the component can handle authentication checks
    if (!CurrentSession.isSignedIn) {
      return true;
    }

    // Block access for non-members who are signed in
    event.preventDefault();
    event.stopPropagation();
    enqueueSnackbar(
      "This feature keeps our small community thriving! Upgrade to support the app and keep this tool available for everyone",
      {
        variant: "warning",
        persist: true,
        action: (key) => (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Button
              onClick={() => {
                closeSnackbar(key);
                navigate("support");
              }}
              size="small"
              style={{ color: "white" }}
            >
              Upgrade
            </Button>
          </div>
        ),
      }
    );
    AnalyticsService.event("gatekeep_block_feature_access", {
      featureName: featureName,
    });
    return false;
  };

  // Clone the child and add our click handler
  const child = React.Children.only(children);
  const originalOnClick = child.props.onClick;
  const enhancedChild = React.cloneElement(child, {
    onClick: (event) => {
      // If membership is valid and there was an original onClick, call it
      const shouldProceed = handleClick(event);
      if (shouldProceed && originalOnClick) {
        originalOnClick(event);
      }
    },
  });

  return enhancedChild;
};

export default Gatekeep;
